import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";

import { OptionType, SharingRights } from "@/shared/utils/constants";
import {
  SurveyViewFormContentListProps,
  QuestionWithAnswers,
  AnswerWithOption,
} from "../types";
import BaseCheckbox from "@/components_v2/FormFields/Checkbox/BaseCheckbox";
import BaseTag from "@/components_v2/Tags/BaseTag";
import {
  AnswerResponse,
  OptionResponse,
  QuestionResponse,
} from "@/redux/survey/types";
import {
  useLazyGetAnswersQuery,
  useLazyGetQuestionsQuery,
} from "@/redux/service";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { SurveyViewPublicationDestination } from "@/redux/survey-views/types";
import { getEvidenceTitle } from "@/domains/evidence/hooks/useEvidenceFields";
import { useOrganizationId } from "@/shared/hooks/useOrganizationId";

import "../styles.css";

const SurveyViewFormContentList = ({
  form,
  sectionIds,
}: SurveyViewFormContentListProps) => {
  const { t } = useTranslation();

  const [getQuestions, questionsResponse] = useLazyGetQuestionsQuery();
  const [getAnswers, answersResponse] = useLazyGetAnswersQuery();
  const [isExpanded, setIsExpanded] = useState(false);
  const currentOrganizationId = useOrganizationId();

  const answers = useWatch({ control: form.control, name: "answers" });
  const publicationDestination = useWatch({
    control: form.control,
    name: "config.publicationDestination",
  });

  const selectedAnswerIds = useMemo(
    () => new Set(answers.map((answer) => answer.answer)),
    [answers],
  );

  const answerBlocksMap = useMemo(() => {
    const map = new Map();
    answers.forEach((answer) => {
      map.set(
        answer.answer,
        new Set(answer.attachedBlocks.map((block) => block.id)),
      );
    });
    return map;
  }, [answers]);

  useEffect(() => {
    if (sectionIds) {
      getQuestions({ sections: sectionIds, pagination: "off" });
      getAnswers({
        sections: sectionIds,
        organization: currentOrganizationId,
        pagination: "off",
      });
    }
  }, [sectionIds, getQuestions, getAnswers]);

  const handleQuestionSelect = (
    selectedQuestionId: number,
    checked: boolean,
  ) => {
    const answersData = answersResponse.data as AnswerResponse[];
    const questionAnswers = answersData.filter(
      (answer) => answer.question === selectedQuestionId,
    );

    if (checked) {
      const newlySelectedAnswers = questionAnswers
        .filter((answer) => !selectedAnswerIds.has(answer.id))
        .map((answer: AnswerResponse) => ({
          answer: answer.id,
          question: answer.question,
          attachedBlocks: answer.attachedBlocks.map((block) => ({
            id: block.id,
            sharingRights: block.sharingRights,
          })),
        }));
      if (newlySelectedAnswers.length !== 0) {
        form.setValue("answers", [...answers, ...newlySelectedAnswers]);
      }
    } else {
      const unselectedAnswerIds = new Set(
        questionAnswers.map((answer) => answer.id),
      );
      const filteredAnswers = answers.filter(
        (answer) => !unselectedAnswerIds.has(answer.answer),
      );
      form.setValue("answers", filteredAnswers);
    }
  };

  const handleAnswerSelect = (selectedAnswerId: number, checked: boolean) => {
    if (checked) {
      const answersData = answersResponse.data as AnswerResponse[];
      const selectedAnswer = answersData.find(
        (answer) => answer.id === selectedAnswerId,
      );
      if (selectedAnswer) {
        const newAnswer = {
          answer: selectedAnswer.id,
          question: selectedAnswer.question,
          attachedBlocks: selectedAnswer.attachedBlocks.map((block) => ({
            id: block.id,
            sharingRights: block.sharingRights,
          })),
        };
        form.setValue("answers", [...answers, newAnswer]);
      }
    } else {
      const filteredAnswers = answers.filter(
        (answer) => answer.answer !== selectedAnswerId,
      );
      form.setValue("answers", filteredAnswers);
    }
  };

  const handleDataBlockSelect = (
    selectedAnswerId: number,
    selectedBlockId: number,
    selectedBlockSharingRights: string,
    checked: boolean,
  ) => {
    const answerIndex = answers.findIndex(
      (answer) => answer.answer === selectedAnswerId,
    );
    if (answerIndex >= 0) {
      // Answer exists, modify blocks
      const currentAnswer = answers[answerIndex];
      if (!currentAnswer) return;

      const hasBlock = currentAnswer.attachedBlocks.some(
        (block) => block.id === selectedBlockId,
      );
      if (checked && !hasBlock) {
        // Add block
        const updatedAnswer = {
          ...currentAnswer,
          attachedBlocks: [
            ...currentAnswer.attachedBlocks,
            { id: selectedBlockId, sharingRights: selectedBlockSharingRights },
          ],
        };
        const updatedAnswers = answers.with(answerIndex, updatedAnswer);
        form.setValue("answers", updatedAnswers);
      } else if (!checked && hasBlock) {
        const updatedBlocks = currentAnswer.attachedBlocks.filter(
          (block) => block.id !== selectedBlockId,
        );

        if (updatedBlocks.length === 0) {
          // Remove entire answer if no blocks left
          const updatedAnswers = answers.filter(
            (_, index) => index !== answerIndex,
          );
          form.setValue("answers", updatedAnswers);
        } else {
          // Update answer with remaining blocks
          const updatedAnswer = {
            ...currentAnswer,
            attachedBlocks: updatedBlocks,
          };
          const updatedAnswers = answers.with(answerIndex, updatedAnswer);
          form.setValue("answers", updatedAnswers);
        }
      }
    } else if (checked) {
      // Answer doesn't exist, create new one
      const answersData = answersResponse.data as AnswerResponse[];
      const selectedAnswer = answersData.find(
        (answer) => answer.id === selectedAnswerId,
      );

      if (selectedAnswer) {
        const newAnswer = {
          answer: selectedAnswer.id,
          question: selectedAnswer.question,
          attachedBlocks: [
            { id: selectedBlockId, sharingRights: selectedBlockSharingRights },
          ],
        };
        form.setValue("answers", [...answers, newAnswer]);
      }
    }
  };

  const questionsWithAnswers = useMemo(() => {
    if (!questionsResponse.isSuccess || !answersResponse.isSuccess) return [];

    const questionsData = questionsResponse.data as QuestionResponse[];
    const answersData = answersResponse.data as AnswerResponse[];
    return questionsData.map((question: QuestionResponse) => {
      const questionsAnswers = answersData.filter(
        (a: AnswerResponse) => a.question === question.id,
      );
      return {
        question,
        answers: questionsAnswers
          .map((answer: AnswerResponse) => {
            const selectedOption = question.options.find(
              (opt: OptionResponse) => opt.id === answer.option,
            );
            if (!selectedOption) return;
            return {
              ...answer,
              option: selectedOption,
            };
          })
          .filter((answer) => answer != null),
      };
    });
  }, [questionsResponse, answersResponse]);

  const getIsQuestionSelected = useCallback(
    (questionId: number) => {
      if (!answersResponse.isSuccess) return false;

      const answersData = answersResponse.data as AnswerResponse[];
      const questionAnswers = answersData.filter(
        (answer: AnswerResponse) => answer.question === questionId,
      );
      return (
        questionAnswers.length !== 0 &&
        questionAnswers.every((answer) => selectedAnswerIds.has(answer.id))
      );
    },
    [answersResponse, selectedAnswerIds, answers],
  );

  const getIsAnswerSelected = useCallback(
    (answerId: number) => {
      return selectedAnswerIds.has(answerId);
    },
    [selectedAnswerIds],
  );

  const getIsDataBlockSelected = useCallback(
    (answerId: number, blockId: number) => {
      const blocks = answerBlocksMap.get(answerId);
      return blocks?.has(blockId) ?? false;
    },
    [answerBlocksMap],
  );

  const generateAnswerLabel = (answer: AnswerWithOption) => {
    if (answer.option.type === OptionType.PROMPT.value) {
      return (
        <div className="flex flex-col gap-200">
          {answer.attachedBlocks.map((block) => (
            <div
              key={`block-${block.id}`}
              className="flex items-center gap-200"
            >
              {getEvidenceTitle(block) || ""}
              <BaseTag
                label={block.template.title}
                size="small"
                color={block.template.color}
              />
              <BaseTag
                label={SharingRights.getLabel(block.sharingRights)}
                size="small"
                className={block.sharingRights.toLowerCase().replace("_", "-")}
              />
            </div>
          ))}
        </div>
      );
    }

    let text = answer.textAnswer;
    if (
      [
        OptionType.SINGLE_CHOICE.value,
        OptionType.MULTIPLE_CHOICE.value,
        OptionType.CHOICE_TEXT.value,
      ].includes(answer.option.type)
    ) {
      text = answer.option.label;
    }

    return (
      <>
        <div className={!isExpanded ? "truncated-text" : undefined}>{text}</div>
        {text.length > 200 && (
          <a
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            className="show-more-button"
          >
            {isExpanded ? t("buttons.showLess") : t("buttons.showMore")}
          </a>
        )}
      </>
    );
  };

  const renderAnswerContent = (
    answer: AnswerWithOption,
    hasMultipleAnswers: boolean,
  ) => {
    const hasAttachedBlocks =
      answer.attachedBlocks && answer.attachedBlocks.length > 0;
    const isPromptType = answer.option.type === OptionType.PROMPT.value;

    if (isPromptType) {
      return renderAttachedDataBlocks(answer);
    }

    if (hasMultipleAnswers || hasAttachedBlocks) {
      return (
        <div key={`answer-${answer.id}`} className="answers-container">
          <BaseCheckbox
            checked={getIsAnswerSelected(answer.id)}
            onChange={(checked) => handleAnswerSelect(answer.id, checked)}
            label={generateAnswerLabel(answer)}
          />
          {hasAttachedBlocks && renderAttachedDataBlocks(answer)}
        </div>
      );
    }

    return (
      <div key={`answer-${answer.id}`} className="answers-container">
        <div>{generateAnswerLabel(answer)}</div>
        {hasAttachedBlocks && renderAttachedDataBlocks(answer)}
      </div>
    );
  };

  const renderAttachedDataBlocks = (answer: AnswerWithOption) => {
    return (
      <div key={`answer-${answer.id}`} className="flex flex-col gap-200">
        {answer.attachedBlocks.map((block) => {
          const blockCheckboxId = `evidence-${answer.id}-${block.id}`;

          return (
            <div key={blockCheckboxId} className="answers-container">
              <BaseCheckbox
                checked={
                  (publicationDestination ===
                    SurveyViewPublicationDestination.WEBSITE &&
                    block.sharingRights === SharingRights.PUBLIC.value) ||
                  getIsDataBlockSelected(answer.id, block.id)
                }
                disabled={
                  publicationDestination ===
                    SurveyViewPublicationDestination.WEBSITE &&
                  block.sharingRights === SharingRights.PUBLIC.value
                }
                onChange={(checked) =>
                  handleDataBlockSelect(
                    answer.id,
                    block.id,
                    block.sharingRights,
                    checked,
                  )
                }
                label={
                  <div className="flex items-center gap-200">
                    {getEvidenceTitle(block) || ""}
                    <BaseTag
                      label={block.template.title}
                      size="small"
                      color={block.template.color}
                    />

                    <BaseTag
                      label={SharingRights.getLabel(block.sharingRights)}
                      size="small"
                      className={block.sharingRights
                        .toLowerCase()
                        .replace("_", "-")}
                    />
                  </div>
                }
              />
            </div>
          );
        })}
      </div>
    );
  };

  const getIsQuestionDisabled = useCallback(
    (question: QuestionWithAnswers) => {
      if (question.answers.length === 0) return true;

      if (publicationDestination === SurveyViewPublicationDestination.WEBSITE) {
        const selectedAnswer = answers.find(
          (selected) => selected.question === question.question.id,
        );

        if (!selectedAnswer) return false;
        const answerData = question.answers.find(
          (answer) => answer.id === selectedAnswer.answer,
        );

        if (!answerData) return false;
        const selectedBlockIds = new Set(
          selectedAnswer.attachedBlocks.map((block) => block.id),
        );
        const selectedBlocks = answerData.attachedBlocks.filter((block) =>
          selectedBlockIds.has(block.id),
        );

        return (
          selectedBlocks.length > 0 &&
          selectedBlocks.every(
            (block) => block.sharingRights === SharingRights.PUBLIC.value,
          )
        );
      }
      return false;
    },
    [answers, publicationDestination, selectedAnswerIds],
  );

  return (
    <div>
      {(questionsResponse.isLoading || answersResponse.isLoading) && (
        <BaseSpinner />
      )}

      <div>
        {questionsWithAnswers.map((item: QuestionWithAnswers) => {
          const hasMultipleAnswers = item.answers.length > 1;

          return (
            <div
              key={item.question.id}
              className="question-container flex flex-col gap-250"
            >
              <div>
                <BaseCheckbox
                  checked={getIsQuestionSelected(item.question.id)}
                  disabled={
                    getIsQuestionDisabled(item) || item.answers.length === 0
                  }
                  onChange={(checked) =>
                    handleQuestionSelect(item.question.id, checked)
                  }
                  label={item.question?.label || ""}
                />
              </div>
              {item.answers.length === 0 ? (
                <span>{t("surveyViews.labels.noAnswersAvailable")}</span>
              ) : (
                item.answers.map((answer) =>
                  renderAnswerContent(answer, hasMultipleAnswers),
                )
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SurveyViewFormContentList;
